import { $ } from '../../../../test/utilities/jQuerySource';
import {
    ATTR_NAME_RESOURCE_NAME,
    loadScript,
    loadStylesheet,
} from '../../../scriptLoader';
import { CSS_MIME_TYPE, JS_MIME_TYPE } from '../../../utility/mime.helpers';
import { queryTextFromUrl } from '../../helpers/apiHelpers';
import {
    paramAllowExternal,
    paramName,
    paramType,
    paramUnload,
    paramUseProxy,
} from '../constants/parameterNames';
import { typeBoolean, typeOption } from '../constants/parameterTypes';
import { success } from '../helpers/commandResult';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';

export const TYPE_SCRIPT = 'script';
export const TYPE_STYLE = 'style';

const requiredParameters = [
    {
        name: paramName,
        description: 'The name of resource',
    },
];
const optionalParameters = [
    {
        name: paramType,
        description: `If specified, must be either "${TYPE_SCRIPT}" or "${TYPE_STYLE}" - the type of the resource`,
        type: typeOption,
        options: [
            {
                name: 'Script',
                value: TYPE_SCRIPT,
            },
            {
                name: 'Style',
                value: TYPE_STYLE,
            },
        ],
    },
    {
        name: paramUnload,
        description:
            'If true, indicates that the resource should be unloaded (defaults - false)',
        type: typeBoolean,
    },
    {
        name: paramAllowExternal,
        description:
            'If true, loads the name value as an external resource (defaults - false)',
        type: typeBoolean,
    },
    {
        name: paramUseProxy,
        description:
            '(boolean, defaults to false) If true, loads the resource via the FormsViewer server instead of accessing the URL directly. Useful when loading resources from SharePoint.',
        type: typeBoolean,
    },
];

const extensionTypeMap = {
    css: TYPE_STYLE,
    js: TYPE_SCRIPT,
};
const validTypes = Object.values(extensionTypeMap);
const errorCouldNotDetermineType = `Could not determine resource type based on name '{0}'. Please use the /${paramType} parameter.`;
const errorUnloadMessage =
    "Could not unload resource with name: {0}  A resource with this name was not found in the page's DOM";
const description = 'Allows loading a CSS or JS file into the page.';

const invalidTypeError = (type) =>
    new Error(
        `Invalid type: '${type}'. Valid types are: ${validTypes.join(', ')}`,
    );

export function getType(name) {
    if (!name.includes('.')) {
        return '';
    }
    const extension = name.split('.').pop().toLowerCase();

    return extensionTypeMap[extension] || '';
}

function unloadResource(name) {
    const resources = $(document.head).find(
        String.format('[{0}="{1}"]', ATTR_NAME_RESOURCE_NAME, name),
    );

    if (!resources.length) {
        throw new Error(String.format(errorUnloadMessage, name));
    }

    resources.remove();
}

function normalizeType(type, resourceName) {
    if (!type) {
        return getType(resourceName);
    }

    return type.toLowerCase();
}

function validateType(type, fileName) {
    if (!type) {
        throw new Error(String.format(errorCouldNotDetermineType, fileName));
    }

    if (!validTypes.includes(type)) {
        throw invalidTypeError(type);
    }
}

function getMimeTypeForType(type) {
    switch (type) {
        case TYPE_SCRIPT:
            return JS_MIME_TYPE;
        case TYPE_STYLE:
            return CSS_MIME_TYPE;
        default:
            throw invalidTypeError(type);
    }
}

async function performLoad(type, name, resourceUrl) {
    try {
        switch (type) {
            case TYPE_SCRIPT:
                await loadScript(resourceUrl);
                return;
            case TYPE_STYLE:
                await loadStylesheet(resourceUrl, {
                    resourceName: name,
                });
                return;
            default:
                throw invalidTypeError(type);
        }
    } catch (e) {
        throw new Error('Error occurred loading resource: ' + e);
    }
}

// (String, QfsApi, String) -> Promise[ObjectUrl]
const getResourceContentByProxy = async (name, api, type) => {
    const resourceText = await queryTextFromUrl(name, api);
    const mimeType = getMimeTypeForType(type);
    const blob = new Blob([resourceText], { type: mimeType });
    const objectUrl = URL.createObjectURL(blob);

    return objectUrl;
};

const getResourceUrl = async (name, isExternal, template, api, proxy, type) => {
    if (proxy) {
        const resourceUrl = await getResourceContentByProxy(name, api, type);

        return {
            url: resourceUrl,
            isObjectUrl: true,
        };
    }

    if (isExternal) {
        return {
            url: name,
            isObjectUrl: false,
        };
    }

    // form resource file
    return {
        url: template.getResourceAbsoluteUrl(name),
        isObjectUrl: false,
    };
};

export const validateParameters = ({
    name,
    allowExternal,
    unload,
    proxy,
    isExternal,
}) => {
    if (proxy && !allowExternal) {
        throw new Error(
            `/${paramAllowExternal} must be true if /${paramUseProxy} is true`,
        );
    }

    if (!isExternal && !unload && (name.includes('\\') || name.includes('/'))) {
        throw new Error(
            `Could not load resource. Invalid path in /name: ${name}`,
        );
    }
};

function LoadResource() {
    async function executeAsync({ api, parameters, template }) {
        const name = getParamValue(parameters, paramName);
        const type = normalizeType(getParamValue(parameters, paramType), name);
        const unload = getBoolParamValue(parameters, paramUnload, false);
        const allowExternal = getBoolParamValue(
            parameters,
            paramAllowExternal,
            false,
        );
        const proxy = getBoolParamValue(parameters, paramUseProxy, false);
        const isExternal = allowExternal && name.includes('/');

        validateParameters({ name, allowExternal, unload, proxy, isExternal });

        if (unload) {
            unloadResource(name);
        } else {
            validateType(type, name);

            const { url, isObjectUrl } = await getResourceUrl(
                name,
                isExternal,
                template,
                api,
                proxy,
                type,
            );

            try {
                await performLoad(type, name, url, isObjectUrl);
            } finally {
                if (isObjectUrl) {
                    URL.revokeObjectURL(url);
                }
            }
        }

        return success();
    }

    return {
        description,
        executeAsync,
        optionalParameters,
        requiredParameters,
    };
}

export default LoadResource;
